import { Workbook } from 'exceljs'
import moment from 'moment'

import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { DropdownItem, DropdownMenu, ExportIconImg, IconImg } from './DropDownFormExport.styled'

import ExcelIcon from '@assets/images/excel-icon.svg'
import PdfIcon from '@assets/images/pdf-icon.svg'
import UploadIcon from '@assets/images/upload-icon.svg'

import { useLazyForm } from '@hooks/form/form.hook'

import { ExportType } from '@utils/hooks'

import { Button } from '@components/Button'
import { DropDown } from '@components/DropDown'

import { Form, FormModelStructure } from '@types'

import {
  FlattenedField,
  flattenFormField,
} from '@modules/project-form-models/components/FormBuilder/FormBuilder.helper'

import { useReportFormsPdf } from '@hooks/report/report-forms.hook'

type DropDownFormExportProps = {
  formId: string
}

export const DropDownFormExport: FC<DropDownFormExportProps> = ({ formId }) => {
  const { t } = useTranslation()

  const { getData } = useLazyForm()
  const { downloadPdf, status } = useReportFormsPdf(formId)

  const exportOptions: {
    id: ExportType | 'csv'
    label: string
    icon: string
    onClick: () => void
  }[] = [
    {
      id: ExportType.PDF,
      label: 'PDF',
      icon: PdfIcon,
      onClick: () => handleExport(ExportType.PDF),
    },
    { id: 'csv', label: 'Excel', icon: ExcelIcon, onClick: () => handleExport('csv') },
  ]

  const getAllFields = (formModel: FormModelStructure): FlattenedField[] => {
    const processField = (field: FlattenedField): FlattenedField[] => {
      if ('arrayFields' in field) {
        const arrayResult: FlattenedField[] = []
        ;(field.arrayFields as FlattenedField[]).forEach((arrayField) => {
          if ('gridFields' in arrayField) {
            arrayResult.push(...(arrayField.gridFields as FlattenedField[]))
          } else {
            arrayResult.push(arrayField)
          }
        })
        return arrayResult
      } else if ('gridFields' in field) {
        return field.gridFields as FlattenedField[]
      } else {
        return [field]
      }
    }

    const result: FlattenedField[] = []
    formModel.fields.forEach((field) => {
      const flatField = flattenFormField(field)
      result.push(...processField(flatField))
    })

    return result
  }

  const exportFormToExcel = async (form: Form) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Form Data')
    const metadataWorksheet = workbook.addWorksheet('Form Metadata')

    // Configure Form Data worksheet
    const fields = getAllFields(form.formModel)

    // Add headers
    worksheet.columns = [
      { header: 'Field ID', width: 40 },
      { header: 'Field Type', width: 15 },
      { header: 'Label', width: 40 },
      { header: 'Required', width: 10 },
      { header: 'Value', width: 70 },
      { header: 'Options', width: 40 },
    ]

    // Add data rows
    fields.forEach((field) => {
      worksheet.addRow([
        field.fieldId,
        field.fieldType,
        field.label,
        field.required?.toString() || '',
        Array.isArray(field.value) ? field.value.join(', ') : field.value || '',
        field.options || '',
      ])
    })

    // Style the header row
    const headerRow = worksheet.getRow(1)
    headerRow.font = { bold: true }

    // Configure Metadata worksheet
    metadataWorksheet.columns = [
      { header: 'Property', width: 15 },
      { header: 'Value', width: 50 },
    ]

    // Add metadata
    const metadataRows = [
      ['Form ID', form.id],
      ['Form Name', form.name],
      ['Created Date', form.createdDate],
      ['Status', form.status],
      ['Author', `${form.author.firstName} ${form.author.lastName}`],
      ['Author Email', form.author.email],
      ['Company', form.company?.name || ''],
      ['Trade', form.trade.label],
      ['Phase', form.phase?.label || ''],
    ]

    metadataRows.forEach((row) => {
      metadataWorksheet.addRow(row)
    })

    // Style metadata header
    const metadataHeaderRow = metadataWorksheet.getRow(1)
    metadataHeaderRow.font = { bold: true }

    // Generate timestamp and filename
    const timestamp = moment().format('YYYY-MM-DD_HH-mm')
    const filename = `${form.name.replace(' ', '-')}_${timestamp}.xlsx`

    // Write to file
    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)

    // Create download link and trigger download
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleExport = async (type: ExportType | 'csv') => {
    const { data } = await getData(formId)

    if (!data?.form) return

    if (type === ExportType.PDF) {
      downloadPdf(formId)
      return
    }
    if (type === 'csv') {
      await exportFormToExcel(data.form)
      return
    }
  }

  return (
    <DropDown
      disabled={status === 'loading'}
      WrappedComponent={() => (
        <Button isLoading={status === 'loading'}>
          <ExportIconImg src={UploadIcon} />
          {t('modules_project-settings_components_Planning_export')}
        </Button>
      )}
      WrappedChildren={({ hide }) => (
        <DropdownMenu
          onMouseMove={(event) => {
            event.stopPropagation()
          }}
        >
          {exportOptions.map(({ id, label, icon, onClick }) => (
            <DropdownItem
              key={id}
              onClick={() => {
                onClick()
                hide()
              }}
            >
              <IconImg src={icon} alt={label} />
              {label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
    />
  )
}
